/**
 * Tool-call parameter input with a fixed/expression mode toggle and an
 * optional JSON mode. The toggle is purely an editor affordance — the
 * executor resolves ``{{ … }}`` regardless of the stored source. While the
 * text is not valid JSON the previous value stays committed.
 */

import { FC, KeyboardEvent, useRef, useState } from 'react'
import { useTranslate } from 'react-admin'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import DataObjectIcon from '@mui/icons-material/DataObject'
import { JsonValue, ParameterMapping } from '../types'
import { themeColor, xxllncColor } from '../../../layout/theme/color'
import { WORKFLOW } from '../tokens'
import ExpressionTextField from '../autocomplete/ExpressionTextField'
import ExpressionInsertPill from '../components/ExpressionInsertPill'
import FormatJsonPill from '../components/FormatJsonPill'
import { applyEditorKey, MONO_FONT, monoInputSx } from './jsonEditing'
import {
  deriveFieldMode,
  FieldMode,
  isJsonMode,
  looksLikeJsonTemplate,
  valueToText,
} from './parameterUtils'

// Re-export for existing consumers (AiCallConfig, ParseConfig, TestToolCallDialog).
export { valueToText } from './parameterUtils'

interface ParameterValueFieldProps {
  label: string
  paramType: string
  paramDesc: string
  paramRequired: boolean
  mapping?: ParameterMapping
  onChange: (mapping: ParameterMapping) => void
}

const metaPillSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  px: 0.75,
  py: 0.25,
  borderRadius: '6px',
} as const

const ParameterValueField: FC<ParameterValueFieldProps> = ({
  label,
  paramType,
  paramDesc,
  paramRequired,
  mapping,
  onChange,
}) => {
  const translate = useTranslate()
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
  const [mode, setMode] = useState<FieldMode>(() => deriveFieldMode(mapping))
  const [jsonMode, setJsonMode] = useState<boolean>(() => isJsonMode(mapping))
  const [text, setText] = useState<string>(() => valueToText(mapping?.value))
  const [jsonError, setJsonError] = useState(false)

  const commit = (value: JsonValue, asJson: boolean, asExpression: boolean) =>
    onChange({
      source: asExpression ? 'expression' : 'hardcoded',
      value,
      jsonMode: asJson || undefined,
    })

  const applyText = (raw: string, asJson: boolean, asExpression: boolean = mode === 'expression') => {
    setText(raw)
    if (asExpression) {
      setJsonError(false)
      commit(raw, false, true)
      return
    }
    // Typing an expression in a fixed field flips to expression mode; JSON-shaped
    // text stays fixed so objects containing expressions aren't sent as strings.
    if (!asJson && raw.includes('{{') && !looksLikeJsonTemplate(raw)) {
      setMode('expression')
      setJsonError(false)
      commit(raw, false, true)
      return
    }
    if (!asJson) {
      setJsonError(false)
      commit(raw, false, false)
      return
    }
    if (raw.trim() === '') {
      setJsonError(false)
      commit('', true, false)
      return
    }
    try {
      commit(JSON.parse(raw) as JsonValue, true, false)
      setJsonError(false)
    } catch {
      setJsonError(true)
    }
  }

  const focusAt = (caret: number) => {
    requestAnimationFrame(() => {
      const el = inputRef.current
      if (el) {
        el.focus()
        el.setSelectionRange(caret, caret)
      }
    })
  }

  const handleModeChange = (next: FieldMode | null) => {
    if (!next || next === mode) return
    setMode(next)
    setJsonError(false)
    if (next === 'expression') {
      setJsonMode(false)
      if (text.trim() === '') {
        const seeded = '{{  }}'
        setText(seeded)
        commit(seeded, false, true)
        focusAt(3)
        return
      }
      commit(text, false, true)
      return
    }
    commit(text, false, false)
  }

  const handleJsonToggle = () => {
    const next = !jsonMode
    setJsonMode(next)
    applyText(text, next, false)
  }

  const handleFormat = () => {
    try {
      const pretty = JSON.stringify(JSON.parse(text) as JsonValue, null, 2)
      applyText(pretty, true, false)
    } catch {
      // Not valid JSON — the error state already tells the user.
    }
  }

  const handleInsertBraces = () => {
    const el = inputRef.current
    const start = el?.selectionStart ?? text.length
    const end = el?.selectionEnd ?? start
    const next = `${text.slice(0, start)}{{  }}${text.slice(end)}`
    if (mode !== 'expression') {
      setMode('expression')
      setJsonMode(false)
    }
    setText(next)
    setJsonError(false)
    commit(next, false, true)
    focusAt(start + 3)
  }

  const handleEditorKeys = (e: KeyboardEvent<HTMLDivElement>) => {
    const el = e.target as HTMLTextAreaElement
    if (typeof el.selectionStart !== 'number') return
    const start = el.selectionStart
    const end = el.selectionEnd ?? start
    const edit = applyEditorKey(text, e.key, e.shiftKey, start, end)
    if (edit === null) return
    e.preventDefault()
    if (edit === 'consume') return
    applyText(edit.text, true, false)
    requestAnimationFrame(() => el.setSelectionRange(edit.caret, edit.caret))
  }

  const isExpression = mode === 'expression'
  const showFormat = !isExpression && jsonMode && !jsonError && text.trim() !== ''
  const monoStyling = jsonMode || isExpression

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <ToggleButtonGroup
        value={mode}
        exclusive
        size="small"
        onChange={(_e, val: FieldMode | null) => handleModeChange(val)}
        sx={{ alignSelf: 'flex-start' }}
      >
        <ToggleButton
          value="fixed"
          sx={{ textTransform: 'none', fontSize: 11, lineHeight: 1, px: 1, py: 0.5 }}
        >
          {translate('workflow.config.paramSourceFixed')}
        </ToggleButton>
        <ToggleButton
          value="expression"
          sx={{ textTransform: 'none', fontSize: 11, lineHeight: 1, px: 1, py: 0.5 }}
        >
          {translate('workflow.config.paramSourceExpression')}
        </ToggleButton>
      </ToggleButtonGroup>

      <ExpressionTextField
        label={label}
        required={paramRequired}
        value={text}
        onChange={(e) => applyText(e.target.value, jsonMode)}
        onKeyDown={!isExpression && jsonMode ? handleEditorKeys : undefined}
        size="small"
        fullWidth
        helperText={
          paramDesc || translate('workflow.config.paramNoDescription', { type: paramType })
        }
        multiline={monoStyling}
        minRows={jsonMode ? 3 : 1}
        error={jsonError}
        inputRef={(el: HTMLInputElement | HTMLTextAreaElement | null) => {
          inputRef.current = el
        }}
        InputProps={monoStyling ? { sx: monoInputSx } : undefined}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 0.25, minHeight: 22 }}>
        <Tooltip title={paramRequired ? translate('workflow.config.paramRequired') : ''}>
          <Box component="span" sx={{ ...metaPillSx, background: themeColor.slate10 }}>
            <Typography
              component="span"
              sx={{
                fontSize: '0.65rem',
                fontWeight: 700,
                lineHeight: 1,
                color: themeColor.slate60,
                fontFamily: MONO_FONT,
              }}
            >
              {paramType}
              {paramRequired ? ' *' : ''}
            </Typography>
          </Box>
        </Tooltip>

        {!isExpression && (
          <Tooltip title={translate('workflow.config.paramJsonHelp')}>
            <ButtonBase
              onClick={handleJsonToggle}
              sx={{
                ...metaPillSx,
                'border': `1px solid ${jsonMode ? WORKFLOW.tool_call.border : themeColor.slate20}`,
                'background': jsonMode ? WORKFLOW.tool_call.bg : 'transparent',
                'color': jsonMode ? WORKFLOW.tool_call.accent : themeColor.slate50,
                'transition': 'all 120ms ease',
                '&:hover': {
                  borderColor: jsonMode ? WORKFLOW.tool_call.border : themeColor.slate40,
                  color: jsonMode ? WORKFLOW.tool_call.accent : themeColor.slate60,
                },
              }}
            >
              <DataObjectIcon sx={{ fontSize: 12 }} />
              <Typography
                component="span"
                sx={{ fontSize: '0.65rem', fontWeight: 700, lineHeight: 1 }}
              >
                JSON
              </Typography>
            </ButtonBase>
          </Tooltip>
        )}

        <ExpressionInsertPill onInsert={handleInsertBraces} />

        {showFormat && <FormatJsonPill onFormat={handleFormat} />}

        {jsonError && (
          <Typography
            sx={{
              flex: 1,
              textAlign: 'right',
              fontSize: '0.65rem',
              fontWeight: 500,
              lineHeight: 1.2,
              color: xxllncColor.red100,
            }}
          >
            {translate('workflow.config.paramJsonInvalid')}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export default ParameterValueField
